import type {
  Business,
  LibrarySortKey,
  OpenFilter,
  Prospect,
  ResultFilter,
  SortKey,
} from '../types';
import { getOpenStatus } from './openingHours';
import { ASSESSMENT_RANK } from './labels';
import { assessmentOf } from './assessment';

export interface FilterOptions {
  filter: ResultFilter;
  search: string;
  sort: SortKey;
  /** Filtre horaires, calcule localement : aucun appel Google. */
  openFilter?: OpenFilter;
  /** Injectable pour les tests : "maintenant" doit rester deterministe. */
  now?: Date;
}

/** Normalise pour une recherche insensible a la casse et aux accents. */
export function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

function matchesFilter(business: Business, filter: ResultFilter): boolean {
  switch (filter) {
    case 'without-website':
      return business.websiteType === 'NONE';
    case 'social-only':
      return business.websiteType === 'SOCIAL';
    case 'platform-only':
      return business.websiteType === 'PLATFORM';
    case 'own-website':
      return business.websiteType === 'OWN_WEBSITE';
    case 'priority':
      return business.prospectPriority === 'HIGH';
    case 'with-phone':
      return business.contactable;
    case 'all':
    default:
      return true;
  }
}

/** Le filtre horaires n'appelle jamais Google : tout est deja en memoire. */
export function matchesOpenFilter(
  hours: Business['openingHours'],
  filter: OpenFilter,
  now: Date,
): boolean {
  if (filter === 'all') return true;

  const { state } = getOpenStatus(hours, now);
  if (filter === 'open') return state === 'OPEN';
  if (filter === 'closed') return state === 'CLOSED';
  return state === 'UNKNOWN';
}

const collator = new Intl.Collator('fr', { sensitivity: 'base' });

/**
 * « Je m'en suis deja occupe » : la fiche descend en bas de liste.
 *
 * `SAVED` est deja dans la bibliotheque, `TREATED` en est sortie apres
 * traitement, `CONFIRMED` a un rendez-vous fixe — c'est le cas le plus avance
 * de tous, donc le moins utile a re-prospecter. Dans les trois cas l'entreprise
 * est connue, alors qu'une recherche sert justement a trouver ce qu'on ne
 * connait pas encore. `IGNORED` n'a pas
 * besoin d'y figurer : il est masque en amont, sauf demande explicite, auquel
 * cas le laisser a sa place est le comportement attendu.
 *
 * Ce classement s'applique a **tous** les tris, pas seulement au tri par
 * score : l'intention ne depend pas du critere de classement choisi. Un tri par
 * nom affiche donc deux blocs alphabetiques, les inconnues puis les connues.
 */
function dejaConnue(business: Business): number {
  return business.libraryStatus === 'SAVED' ||
    business.libraryStatus === 'TREATED' ||
    business.libraryStatus === 'CONFIRMED'
    ? 1
    : 0;
}

/** Tri par defaut : score decroissant, puis distance croissante. */
function compareBusinesses(a: Business, b: Business, sort: SortKey): number {
  // Premier critere, avant le choix de l'utilisateur : les entreprises deja
  // traitees passent derriere, quel que soit le tri demande.
  const connue = dejaConnue(a) - dejaConnue(b);
  if (connue !== 0) return connue;

  switch (sort) {
    case 'name':
      return collator.compare(a.name, b.name);
    case 'distance':
      return a.distanceMeters - b.distanceMeters;
    case 'rating':
      // Les etablissements sans note passent en dernier.
      return (b.rating ?? -1) - (a.rating ?? -1) || a.distanceMeters - b.distanceMeters;
    case 'reviews':
      return (
        (b.userRatingCount ?? -1) - (a.userRatingCount ?? -1) ||
        a.distanceMeters - b.distanceMeters
      );
    case 'score':
    default:
      // Un etablissement sans telephone a un score de 0 : il tombe de lui-meme
      // en bas de liste, sans regle de tri particuliere.
      return b.prospectScore - a.prospectScore || a.distanceMeters - b.distanceMeters;
  }
}

export function applyFilters(
  businesses: readonly Business[],
  { filter, search, sort, openFilter = 'all', now = new Date() }: FilterOptions,
): Business[] {
  const needle = normalize(search);

  const result = businesses.filter((business) => {
    if (!matchesFilter(business, filter)) return false;
    if (!matchesOpenFilter(business.openingHours, openFilter, now)) return false;
    if (needle.length === 0) return true;
    return normalize(business.name).includes(needle);
  });

  return result.sort((a, b) => compareBusinesses(a, b, sort));
}

/**
 * Le jugement humain prime sur l'avis automatique.
 *
 * Une fois que vous avez ouvert le site et tranche, PageSpeed n'a plus voix au
 * chapitre : c'est votre verdict qui place la fiche dans la file. Un site que
 * vous avez juge mauvais passe donc devant tout, meme si Lighthouse le trouve
 * techniquement irreprochable — et l'inverse est vrai aussi.
 */
export function fileRank(business: Business): number {
  switch (business.websiteQuality) {
    case 'BAD':
      return -3;
    case 'GOOD':
      // Juge bon : plus rien a en tirer, il ferme la marche.
      return 10;
    default:
      return ASSESSMENT_RANK[assessmentOf(business)];
  }
}

/**
 * Ordonne la file « A traiter » : le plus prometteur d'abord.
 *
 * A rang egal, on retombe sur des criteres deja connus de l'utilisateur —
 * score commercial puis nombre d'avis — pour que l'ordre reste stable et
 * previsible d'une analyse a l'autre. Le placeId departage en dernier ressort :
 * sans lui, deux fiches identiques pourraient permuter d'un rendu a l'autre.
 */
export function sortToTreat(businesses: readonly Business[]): Business[] {
  return [...businesses].sort(
    (a, b) =>
      fileRank(a) - fileRank(b) ||
      b.prospectScore - a.prospectScore ||
      (b.userRatingCount ?? 0) - (a.userRatingCount ?? 0) ||
      a.placeId.localeCompare(b.placeId),
  );
}

export function countBy(businesses: readonly Business[]): Record<ResultFilter, number> {
  const byType = (type: Business['websiteType']): number =>
    businesses.filter((business) => business.websiteType === type).length;

  return {
    all: businesses.length,
    'without-website': byType('NONE'),
    'social-only': byType('SOCIAL'),
    'platform-only': byType('PLATFORM'),
    'own-website': byType('OWN_WEBSITE'),
    priority: businesses.filter((business) => business.prospectPriority === 'HIGH').length,
    'with-phone': businesses.filter((business) => business.contactable).length,
  };
}

export function countByOpenState(
  businesses: readonly Business[],
  now: Date = new Date(),
): Record<OpenFilter, number> {
  const count = (filter: OpenFilter): number =>
    businesses.filter((business) => matchesOpenFilter(business.openingHours, filter, now)).length;

  return {
    all: businesses.length,
    open: count('open'),
    closed: count('closed'),
    unknown: count('unknown'),
  };
}

/**
 * Valeur du filtre secteur de la bibliotheque.
 * "all", "G:<groupe>" pour toute une categorie, "S:<secteur>" pour un
 * sous-secteur precis. Un seul select suffit ainsi aux deux niveaux.
 */
export type SectorFilterValue = string;

export function matchesSector(prospect: Prospect, value: SectorFilterValue): boolean {
  if (value === 'all') return true;
  if (value.startsWith('G:')) return prospect.sectorGroup === value.slice(2);
  if (value.startsWith('S:')) return prospect.sectorId === value.slice(2);
  return true;
}

export interface LibraryFilterOptions {
  search: string;
  status: 'all' | Prospect['status'];
  sort: LibrarySortKey;
  sector?: SectorFilterValue;
  /** Filtre horaires, calcule localement : aucun appel Google. */
  openFilter?: OpenFilter;
  /** Injectable pour les tests : "maintenant" doit rester deterministe. */
  now?: Date;
}

function compareProspects(a: Prospect, b: Prospect, sort: LibrarySortKey): number {
  switch (sort) {
    case 'saved-at':
      return b.savedAt.localeCompare(a.savedAt);
    case 'follow-up':
      // Les relances planifiees d'abord, de la plus proche a la plus lointaine.
      if (a.nextFollowUpAt === b.nextFollowUpAt) return b.savedAt.localeCompare(a.savedAt);
      if (a.nextFollowUpAt === null) return 1;
      if (b.nextFollowUpAt === null) return -1;
      return a.nextFollowUpAt.localeCompare(b.nextFollowUpAt);
    case 'name':
      return collator.compare(a.name, b.name);
    case 'score':
    default:
      return (b.savedScore ?? b.currentScore) - (a.savedScore ?? a.currentScore);
  }
}

export function applyLibraryFilters(
  prospects: readonly Prospect[],
  {
    search,
    status,
    sort,
    sector = 'all',
    openFilter = 'all',
    now = new Date(),
  }: LibraryFilterOptions,
): Prospect[] {
  const needle = normalize(search);

  return prospects
    .filter((prospect) => {
      if (status !== 'all' && prospect.status !== status) return false;
      if (!matchesSector(prospect, sector)) return false;
      // Horaires deja recues avec la fiche : aucun appel Google pour filtrer.
      if (!matchesOpenFilter(prospect.openingHours, openFilter, now)) return false;
      if (needle.length === 0) return true;
      return normalize(prospect.name).includes(needle);
    })
    .sort((a, b) => compareProspects(a, b, sort));
}
